import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaSearch, FaTimes, FaPlay, FaPause, FaMusic, FaUpload, FaCheck } from 'react-icons/fa';
import { musicAPI } from '../utils/api';
import { toast } from 'sonner';

const MusicPicker = ({ onSelect, onClose, selectedTrack }) => {
  const [tracks, setTracks] = useState([]);
  const [genres, setGenres] = useState([]);
  const [activeGenre, setActiveGenre] = useState(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [playingId, setPlayingId] = useState(null);
  const [tab, setTab] = useState('gdme'); // 'gdme' | 'upload'
  const [uploading, setUploading] = useState(false);
  const audioRef = useRef(null);

  const loadCatalog = useCallback(async () => {
    setLoading(true);
    try {
      const res = await musicAPI.getCatalog(activeGenre, search || undefined);
      setTracks(res.data.tracks);
      setGenres(res.data.genres);
    } catch {
      toast.error('Failed to load music');
    } finally {
      setLoading(false);
    }
  }, [activeGenre, search]);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  const togglePlay = (track) => {
    if (playingId === track.id) {
      if (audioRef.current) audioRef.current.pause();
      setPlayingId(null);
    } else {
      // GDME tracks don't have real audio, show a visual indicator
      setPlayingId(track.id);
      if (track.audio_url && audioRef.current) {
        audioRef.current.src = track.audio_url;
        audioRef.current.play().catch(() => {});
      }
    }
  };

  const selectTrack = (track) => {
    onSelect({
      song_id: track.id,
      song_title: track.title,
      artist: track.artist,
      album_art: track.album_art,
      audio_url: track.audio_url || '',
      genre: track.genre,
    });
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('audio/')) {
      toast.error('Please upload an audio file');
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      toast.error('Audio file must be under 15MB');
      return;
    }
    setUploading(true);
    try {
      const res = await musicAPI.uploadTrack(file);
      const track = res.data;
      selectTrack(track);
      toast.success('Audio uploaded!');
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const formatDuration = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      className="border border-gray-200 rounded-xl bg-white overflow-hidden"
      data-testid="music-picker"
    >
      <audio ref={audioRef} />

      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-gray-100 bg-gray-50">
        <div className="flex items-center space-x-2">
          <img src="/gdme-logo.svg" alt="GDME" className="h-5" />
          <span className="text-xs text-gray-400">Music</span>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600" data-testid="close-music-picker">
          <FaTimes />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-100">
        <button
          onClick={() => setTab('gdme')}
          className={`flex-1 py-2 text-sm font-medium transition-colors ${tab === 'gdme' ? 'text-[#4A90E2] border-b-2 border-[#4A90E2]' : 'text-gray-500'}`}
          data-testid="music-tab-gdme"
        >
          GDME Library
        </button>
        <button
          onClick={() => setTab('upload')}
          className={`flex-1 py-2 text-sm font-medium transition-colors ${tab === 'upload' ? 'text-[#4A90E2] border-b-2 border-[#4A90E2]' : 'text-gray-500'}`}
          data-testid="music-tab-upload"
        >
          Upload Audio
        </button>
      </div>

      {tab === 'gdme' ? (
        <>
          {/* Search */}
          <div className="p-3">
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search songs, artists..."
                className="w-full pl-9 pr-3 py-2 bg-gray-100 rounded-lg text-sm outline-none focus:ring-2 focus:ring-[#4A90E2]/30"
                data-testid="music-search-input"
              />
            </div>
          </div>

          {/* Genres */}
          <div className="px-3 pb-2 flex space-x-2 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
            <button
              onClick={() => setActiveGenre(null)}
              className={`flex-shrink-0 px-3 py-1 rounded-full text-xs font-medium transition-colors ${!activeGenre ? 'bg-[#4A90E2] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              data-testid="genre-all"
            >
              All
            </button>
            {genres.map((g) => (
              <button
                key={g}
                onClick={() => setActiveGenre(g)}
                className={`flex-shrink-0 px-3 py-1 rounded-full text-xs font-medium transition-colors ${activeGenre === g ? 'bg-[#4A90E2] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                data-testid={`genre-${g.toLowerCase()}`}
              >
                {g}
              </button>
            ))}
          </div>

          {/* Track list */}
          <div className="max-h-64 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center text-gray-400 text-sm">Loading tracks...</div>
            ) : tracks.length === 0 ? (
              <div className="p-4 text-center text-gray-400 text-sm">No tracks found</div>
            ) : (
              tracks.map((track) => {
                const isSelected = selectedTrack?.song_id === track.id;
                const isPlaying = playingId === track.id;
                return (
                  <div
                    key={track.id}
                    className={`flex items-center space-x-3 px-3 py-2.5 hover:bg-gray-50 transition-colors cursor-pointer ${isSelected ? 'bg-[#4A90E2]/5 border-l-2 border-[#4A90E2]' : ''}`}
                    data-testid={`track-${track.id}`}
                  >
                    {/* Album art */}
                    <div className="relative w-10 h-10 flex-shrink-0 rounded-lg overflow-hidden group" onClick={() => togglePlay(track)}>
                      <img src={track.album_art} alt={track.title} className="w-full h-full object-cover" />
                      <div className={`absolute inset-0 bg-black/40 flex items-center justify-center ${isPlaying ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} transition-opacity`}>
                        {isPlaying ? <FaPause className="text-white text-xs" /> : <FaPlay className="text-white text-xs ml-0.5" />}
                      </div>
                      {isPlaying && (
                        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#4A90E2]">
                          <div className="h-full bg-white/50 animate-pulse" style={{ width: '60%' }} />
                        </div>
                      )}
                    </div>

                    {/* Track info */}
                    <div className="flex-1 min-w-0" onClick={() => selectTrack(track)}>
                      <p className="text-sm font-medium text-gray-900 truncate">{track.title}</p>
                      <p className="text-xs text-gray-500 truncate">{track.artist} &middot; {formatDuration(track.duration)}</p>
                    </div>

                    {/* Select button */}
                    <button
                      onClick={() => selectTrack(track)}
                      className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center transition-colors ${isSelected ? 'bg-[#4A90E2] text-white' : 'bg-gray-100 text-gray-400 hover:bg-[#4A90E2]/20 hover:text-[#4A90E2]'}`}
                      data-testid={`select-track-${track.id}`}
                    >
                      {isSelected ? <FaCheck className="text-xs" /> : <FaMusic className="text-xs" />}
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </>
      ) : (
        /* Upload tab */
        <div className="p-4">
          <label className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-xl p-8 cursor-pointer hover:border-[#4A90E2]/50 transition-colors" data-testid="upload-audio-label">
            {uploading ? (
              <>
                <div className="w-8 h-8 border-2 border-[#4A90E2] border-t-transparent rounded-full animate-spin mb-2" />
                <p className="text-sm text-gray-500">Uploading...</p>
              </>
            ) : (
              <>
                <FaUpload className="text-2xl text-gray-400 mb-2" />
                <p className="text-sm text-gray-600 font-medium">Upload your audio</p>
                <p className="text-xs text-gray-400 mt-1">MP3, WAV, AAC up to 15MB</p>
              </>
            )}
            <input
              type="file"
              accept="audio/*"
              onChange={handleUpload}
              className="hidden"
              disabled={uploading}
              data-testid="audio-upload-input"
            />
          </label>
        </div>
      )}
    </motion.div>
  );
};

export default MusicPicker;
